import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'

/**
 * VERA-299 — provenance for the PUBLIC certificate stack.
 *
 * Deliberately separate from `useProvenance`, which is the owner's redux-saga-backed
 * hook carrying search / filter / sort / pagination state. A public reader needs none
 * of that — they want the chain, in order, and nothing to operate.
 *
 * The server decides what comes back: `show_provenance` gates the rows and
 * `show_prices` nulls the prices, so this hook never has to know the rules. Passing a
 * `viewSlug` makes a custom link render ITS settings rather than the public view's.
 */
export type PublicProvenanceItem = {
  owner?: { first_name?: string; last_name?: string; name?: string; email?: string; avatar?: string } | null
  owner_type?: string
  ownership_date?: string | null
  price?: number | null
}

export const usePublicProvenance = (
  certificateId?: string,
  viewSlug?: string | null,
  enabled = true
) =>
  useQuery({
    queryKey: ['public-provenance', certificateId, viewSlug ?? null],
    queryFn: async () => {
      const response = await createApiCall({
        url: `/certificates/public/provenance/${certificateId}`,
        params: { limit: 50, offset: 0, ...(viewSlug ? { view: viewSlug } : {}) },
        throwError: true,
      })

      return response.data as { data: PublicProvenanceItem[]; total: number }
    },
    enabled: Boolean(certificateId) && enabled,
  })
